import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { AnimatePresence, motion } from "framer-motion"
import { Copy, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadSwap } from "@/shared/ui/load-swap"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageShell } from "@/shared/ui/page-shell"
import { Skeleton } from "@/shared/ui/skeleton"
import { listItem } from "@/shared/lib/motion"
import {
	buildClientLinks,
	listMcpSessions,
	MCP_SERVER_URL,
	mcpKeys,
	revokeSession,
} from "../api/mcp"

const LINKS = buildClientLinks()

const CLIENTS = [
	{
		label: "Добавить в Claude",
		href: LINKS.claude,
		hint: "Форма коннектора откроется уже заполненной",
	},
	{
		label: "Добавить в ChatGPT",
		href: LINKS.chatgpt,
		hint: "Включите «Режим разработчика», нажмите «Создать» и вставьте адрес",
	},
	{ label: "Добавить в Cursor", href: LINKS.cursor, hint: "Установится в один клик" },
	{ label: "Добавить в VS Code", href: LINKS.vscode, hint: "Установится в один клик" },
]

const STEPS = [
	"Нажмите кнопку своего ИИ-клиента или добавьте коннектор вручную по адресу ниже.",
	"Клиент откроет бэкофис: войдите и нажмите «Разрешить».",
	"Готово. ИИ работает с бэкофисом от вашего имени и с вашими правами.",
]

const RULES = [
	"Читать и править черновики ИИ может сразу.",
	"Публикация, рассылка, удаление, архив и права: только после вашего «да» в чате.",
	"Все действия пишутся в журнал, как если бы их сделали вы.",
]

/** Заглушка подключения: значок, название клиента, даты и кнопка «Отключить». */
function SessionSkeleton() {
	return (
		<li
			aria-hidden="true"
			className="flex items-center gap-3 rounded-2xl bg-card p-4 ring-1 ring-border"
		>
			<Skeleton className="size-5 shrink-0 rounded-md" />
			<div className="min-w-0 flex-1">
				<div className="flex h-5 items-center">
					<Skeleton className="h-3.5 w-48 rounded" />
				</div>
				<div className="flex h-4 items-center">
					<Skeleton className="h-3 w-72 max-w-full rounded" />
				</div>
			</div>
			<Skeleton className="h-9 w-24 rounded-xl" />
		</li>
	)
}

/** Раздел MCP: как подключить ИИ-клиента к бэкофису и какие подключения уже есть. */
export function McpPage() {
	const queryClient = useQueryClient()
	const sessions = useQuery({ queryKey: mcpKeys.sessions, queryFn: listMcpSessions })
	const revoke = useMutation({
		mutationFn: revokeSession,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: mcpKeys.sessions }),
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось отключить")),
	})

	const copy = async (text: string, message: string) => {
		await navigator.clipboard.writeText(text)
		toast.success(message)
	}

	return (
		<PageShell
			eyebrow="ИИ"
			title="MCP"
			subtitle="Подключите Claude или другой ИИ-клиент, и он сможет делать в бэкофисе всё, что можете вы."
			width="narrow"
		>
			<section className="space-y-3 rounded-2xl bg-card p-5 ring-1 ring-border">
				<h2 className="font-semibold">Как подключить</h2>
				<ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
					{STEPS.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ol>
				<div className="grid gap-2 sm:grid-cols-2">
					{CLIENTS.map((client) => (
						<a
							key={client.label}
							href={client.href}
							target={client.href.startsWith("https:") ? "_blank" : undefined}
							rel="noreferrer"
							className="rounded-xl bg-secondary/60 p-3 transition-colors hover:bg-secondary"
						>
							<span className="block text-sm font-medium">{client.label}</span>
							<span className="block text-xs text-muted-foreground">{client.hint}</span>
						</a>
					))}
				</div>
				<div className="flex items-center gap-2 rounded-xl bg-secondary/60 p-2 pl-3">
					<code className="min-w-0 flex-1 truncate text-sm">{MCP_SERVER_URL}</code>
					<Button
						size="sm"
						variant="secondary"
						onClick={() => void copy(MCP_SERVER_URL, "Адрес скопирован")}
					>
						<Copy />
						Копировать
					</Button>
				</div>
				<div className="flex items-center gap-2 rounded-xl bg-secondary/60 p-2 pl-3">
					<code className="min-w-0 flex-1 truncate text-xs">{LINKS.claudeCode}</code>
					<Button
						size="sm"
						variant="secondary"
						onClick={() => void copy(LINKS.claudeCode, "Команда для Claude Code скопирована")}
					>
						<Copy />
						Claude Code
					</Button>
				</div>
				<ul className="space-y-1 text-sm text-muted-foreground">
					{RULES.map((rule) => (
						<li key={rule}>• {rule}</li>
					))}
				</ul>
			</section>

			<section className="mt-8 space-y-3">
				<h2 className="font-semibold">Подключено</h2>
				<LoadSwap
					loading={sessions.isLoading}
					id={sessions.isError ? "error" : sessions.data?.length ? "list" : "empty"}
				>
					{sessions.isLoading ? (
						<ul className="space-y-2 pb-2">
							<SessionSkeleton />
							<SessionSkeleton />
						</ul>
					) : sessions.isError ? (
						<LoadErrorState
							message="Не удалось загрузить подключения."
							onRetry={() => void sessions.refetch()}
						/>
					) : sessions.data?.length ? (
						<ul>
							<AnimatePresence initial={false}>
								{sessions.data.map((session) => (
									<motion.li key={session.id} {...listItem}>
										<div className="pb-2">
											<div className="flex items-center gap-3 rounded-2xl bg-card p-4 ring-1 ring-border">
												<Sparkles className="size-5 shrink-0 text-primary" />
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm font-medium">
														{session.userAgent || "ИИ-клиент"}
													</p>
													<p className="text-xs text-muted-foreground">
														Подключён{" "}
														{format(new Date(session.createdAt), "d MMMM, HH:mm", { locale: ru })},
														был активен{" "}
														{format(new Date(session.lastActiveAt), "d MMMM, HH:mm", {
															locale: ru,
														})}
													</p>
												</div>
												<Button
													size="sm"
													variant="ghost"
													className="text-red-400"
													disabled={revoke.isPending}
													onClick={() => revoke.mutate(session.id)}
												>
													Отключить
												</Button>
											</div>
										</div>
									</motion.li>
								))}
							</AnimatePresence>
						</ul>
					) : (
						<EmptyState icon={Sparkles} title="Пока ничего не подключено">
							Подключение появится здесь, когда вы разрешите доступ в ИИ-клиенте.
						</EmptyState>
					)}
				</LoadSwap>
			</section>
		</PageShell>
	)
}
